// @owners { team: patients }
import { Body, Button, Column, ExternalLink, InputZip, Row } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { fetchServiceAvailability, updateTempSignupUser } from '~shared/actions/auth';
import { PRIVACY_POLICY_URL } from '~shared/constants';
import getURLParam from '~shared/helpers/getURLParam';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared } from '~shared/store';
import { validatePresentWithMessage, validateZipCode } from '~shared/validations';

type Values = {
  zip: string;
};

export type Props = {
  readonly disabled?: boolean;
};

const validateZip = (value: string): true | string => {
  const presentError = validatePresentWithMessage('Zip Code')(value);
  const zipError = validateZipCode(value);
  return presentError || zipError || true;
};

export const ServiceAreaForm = ({ disabled = false }: Props) => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { trackPageView } = useAnalytics();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: { zip: '' },
    mode: 'onSubmit',
  });

  const zipController = useController({
    control,
    name: 'zip',
    rules: { validate: validateZip },
  });

  useEffect(() => {
    const origin = getURLParam('origin', window.location.href);
    trackPageView({
      event: EVENTS.ZIP_VERIFICATION_VIEWED,
      params: { flow: 'sign up', origin },
    });
  }, [trackPageView]);

  const onSubmit = (values: Values) => {
    const url = new URL(window.location.href);

    // Preserve the existing query params
    const params = new URLSearchParams(url.search);
    params.set('zip', values.zip);
    const destination = `${url.pathname}?${params.toString()}`;

    const referrerID = params.get('referred_by_id');

    if (referrerID) {
      dispatch(
        updateTempSignupUser({
          referred_by_id: Number(referrerID),
        }),
      );
    }

    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchServiceAvailability(values.zip));
    navigate(destination);
  };

  return (
    <Column gap="MD">
      <Body>Let's check if we serve your area. What's your zip code?</Body>
      <InputZip
        error={zipController.fieldState.error?.message}
        label="Zip Code"
        onBlur={zipController.field.onBlur}
        onChangeText={zipController.field.onChange}
        placeholder="Zip code"
        value={zipController.field.value}
      />
      <Row centerHorizontally>
        <ExternalLink href={PRIVACY_POLICY_URL}>Privacy Policy</ExternalLink>
      </Row>
      <Button
        width="full"
        type="primary"
        disabled={disabled}
        onPress={handleSubmit(onSubmit)}
      >
        Submit
      </Button>
    </Column>
  );
};
